import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import python from "highlight.js/lib/languages/python";
import javascript from "highlight.js/lib/languages/javascript";
import php from "highlight.js/lib/languages/php";
import "highlight.js/styles/github-dark.min.css";

hljs.registerLanguage("bash", bash);
hljs.registerLanguage("python", python);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("php", php);
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Copy,
  CheckCircle,
  AlertTriangle,
  Key,
  Loader2,
  ExternalLink,
  Code
} from "lucide-react";
import Layout from "@/components/Layout";
import { useMyApiKeysQuery, useMyBrandKitsQuery } from "@/generated/graphql";
import { useToast } from "@/components/ui/use-toast";
import BrandKitScrollRow from "@/components/BrandKitScrollRow";

const ApiPlayground = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: apiKeysData, loading: apiKeysLoading } = useMyApiKeysQuery();
  const { data: brandKitsData, loading: brandKitsLoading } = useMyBrandKitsQuery();

  // State for selected API key and endpoint
  const [selectedApiKey, setSelectedApiKey] = useState<string>("");
  const [activeEndpoint, setActiveEndpoint] = useState("create-project");

  // State for form inputs
  const [createProjectForm, setCreateProjectForm] = useState({
    base_video_url: "https://www.youtube.com/watch?v=W7dB7uJOD-k",
    webhook_url: "https://webhook.site/your-webhook-id",
    name: "My Test Project",
    format: "auto",
    language: "pt-BR",
    prompt: "",
    brand_kit_id: ""
  });

  const [listProjectsForm, setListProjectsForm] = useState({
    limit: "10",
    offset: "0"
  });

  const [getProjectForm, setGetProjectForm] = useState({
    projectId: ""
  });

  const [communityListForm, setCommunityListForm] = useState({
    limit: "10",
    offset: "0",
    search: "",
    channel_id: "",
    tag_id: "",
    sort: "newest",
  });

  const [remixForm, setRemixForm] = useState({
    libraryId: "",
    webhook_url: "",
    auto_export: "false",
  });

  const [createClipForm, setCreateClipForm] = useState({
    projectId: "",
    start_time: "",
    end_time: "",
    name: "",
    auto_render: "true",
  });

  const [renderClipForm, setRenderClipForm] = useState({
    clipId: "",
    brand_kit_id: "",
  });

  const [channelLookupForm, setChannelLookupForm] = useState({
    channelId: "",
  });

  const [channelLibrariesForm, setChannelLibrariesForm] = useState({
    channelId: "",
    limit: "20",
    offset: "0",
    search: "",
    tag_id: "",
    sort: "newest",
  });

  const [checkUrlForm, setCheckUrlForm] = useState({
    url: "",
  });

  // State for responses
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestUrl, setRequestUrl] = useState<string>("");
  const [requestBody, setRequestBody] = useState<string>("");
  const [activeLanguage, setActiveLanguage] = useState("curl");

  const apiBaseUrl = import.meta.env.VITE_API_URL;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t('apiPlayground.copiedToClipboard'),
      description: t('apiPlayground.copiedDescription'),
    });
  };

  const makeApiRequest = async (method: string, endpoint: string, body?: any) => {
    if (!selectedApiKey) {
      setError(t('apiPlayground.selectApiKeyFirst'));
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    const url = `${apiBaseUrl}${endpoint}`;
    setRequestUrl(url);
    setRequestBody(body ? JSON.stringify(body, null, 2) : "");

    try {
      const headers: Record<string, string> = {
        'Authorization': `API-Key ${selectedApiKey}`,
        'Content-Type': 'application/json'
      };

      const requestOptions: RequestInit = {
        method,
        headers
      };

      if (body) {
        requestOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, requestOptions);
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseData.error || 'Request failed'}`);
      }

      setResponse(responseData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    makeApiRequest('POST', '/api/v1/project', createProjectForm);
  };

  const handleListProjects = () => {
    const params = new URLSearchParams();
    if (listProjectsForm.limit) params.append('limit', listProjectsForm.limit);
    if (listProjectsForm.offset) params.append('offset', listProjectsForm.offset);

    const endpoint = `/api/v1/projects${params.toString() ? `?${params.toString()}` : ''}`;
    makeApiRequest('GET', endpoint);
  };

  const handleGetProject = () => {
    if (!getProjectForm.projectId) {
      setError(t('apiPlayground.enterProjectId'));
      return;
    }
    makeApiRequest('GET', `/api/v1/project/${getProjectForm.projectId}`);
  };

  const handleListCommunityLibraries = () => {
    const params = new URLSearchParams();
    Object.entries(communityListForm).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    const qs = params.toString() ? `?${params.toString()}` : "";
    makeApiRequest('GET', `/api/v1/community/libraries${qs}`);
  };

  const handleRemixCommunityLibrary = () => {
    if (!remixForm.libraryId) {
      setError("Please enter a community library ID");
      return;
    }
    const body: Record<string, unknown> = {
      auto_export: remixForm.auto_export === "true",
    };
    if (remixForm.webhook_url) body.webhook_url = remixForm.webhook_url;
    makeApiRequest('POST', `/api/v1/community/library/${remixForm.libraryId}/remix`, body);
  };

  const handleCreateProjectClip = () => {
    if (!createClipForm.projectId) {
      setError("Please enter a project ID");
      return;
    }
    const start = parseFloat(createClipForm.start_time);
    const end = parseFloat(createClipForm.end_time);
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      setError("start_time and end_time must be numbers");
      return;
    }
    const body: Record<string, unknown> = {
      start_time: start,
      end_time: end,
      auto_render: createClipForm.auto_render === "true",
    };
    if (createClipForm.name) body.name = createClipForm.name;
    makeApiRequest('POST', `/api/v1/project/${createClipForm.projectId}/clip`, body);
  };

  const handleRenderClip = () => {
    if (!renderClipForm.clipId) {
      setError("Please enter a clip ID");
      return;
    }
    const body: Record<string, unknown> = {};
    if (renderClipForm.brand_kit_id) body.brand_kit_id = renderClipForm.brand_kit_id;
    makeApiRequest('POST', `/api/v1/clip/${renderClipForm.clipId}/render`, body);
  };

  const handleGetCommunityChannel = () => {
    if (!channelLookupForm.channelId) {
      setError("Please enter a channel ID");
      return;
    }
    makeApiRequest('GET', `/api/v1/community/channel/${channelLookupForm.channelId}`);
  };

  const handleListChannelLibraries = () => {
    if (!channelLibrariesForm.channelId) {
      setError("Please enter a channel ID");
      return;
    }
    const params = new URLSearchParams();
    const { channelId, ...rest } = channelLibrariesForm;
    Object.entries(rest).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    const qs = params.toString() ? `?${params.toString()}` : "";
    makeApiRequest('GET', `/api/v1/community/channel/${channelId}/libraries${qs}`);
  };

  const handleCheckCommunityLibrary = () => {
    if (!checkUrlForm.url) {
      setError("Please enter a video URL");
      return;
    }
    const params = new URLSearchParams({ url: checkUrlForm.url });
    makeApiRequest('GET', `/api/v1/community/check?${params.toString()}`);
  };

  const endpoints = [
    {
      id: "create-project",
      name: t('apiPlayground.createProject'),
      method: "POST",
      path: "/api/v1/project",
      description: t('apiPlayground.createProjectDescription')
    },
    {
      id: "list-projects",
      name: t('apiPlayground.listProjects'),
      method: "GET",
      path: "/api/v1/projects",
      description: t('apiPlayground.listProjectsDescription')
    },
    {
      id: "get-project",
      name: t('apiPlayground.getProject'),
      method: "GET",
      path: "/api/v1/project/{id}",
      description: t('apiPlayground.getProjectDescription')
    },
    {
      id: "list-community",
      name: "Community list",
      method: "GET",
      path: "/api/v1/community/libraries",
      description: "Browse community library videos",
    },
    {
      id: "remix-community",
      name: "Remix",
      method: "POST",
      path: "/api/v1/community/library/{id}/remix",
      description: "Create a project from a community library",
    },
    {
      id: "create-clip",
      name: "Manual clip",
      method: "POST",
      path: "/api/v1/project/{id}/clip",
      description: "Create a clip with manual start/end times",
    },
    {
      id: "render-clip",
      name: "Render clip",
      method: "POST",
      path: "/api/v1/clip/{id}/render",
      description: "Queue an existing clip for rendering",
    },
    {
      id: "get-channel",
      name: "Get channel",
      method: "GET",
      path: "/api/v1/community/channel/{id}",
      description: "Fetch a single community channel by id",
    },
    {
      id: "channel-libraries",
      name: "Channel videos",
      method: "GET",
      path: "/api/v1/community/channel/{id}/libraries",
      description: "List videos from a single channel",
    },
    {
      id: "check-url",
      name: "Check URL",
      method: "GET",
      path: "/api/v1/community/check",
      description: "Check if a video URL is already in the community library",
    },
  ];

  const getRequestInfo = () => {
    if (activeEndpoint === "create-project") {
      const body = { ...createProjectForm };
      if (!body.prompt) delete (body as any).prompt;
      if (!body.brand_kit_id) delete (body as any).brand_kit_id;
      return {
        method: "POST",
        url: `${apiBaseUrl}/api/v1/project`,
        body,
        hasBody: true,
      };
    }

    if (activeEndpoint === "list-projects") {
      const params = new URLSearchParams();
      if (listProjectsForm.limit) params.append("limit", listProjectsForm.limit);
      if (listProjectsForm.offset) params.append("offset", listProjectsForm.offset);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return {
        method: "GET",
        url: `${apiBaseUrl}/api/v1/projects${qs}`,
        body: null,
        hasBody: false,
      };
    }

    if (activeEndpoint === "list-community") {
      const params = new URLSearchParams();
      Object.entries(communityListForm).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const qs = params.toString() ? `?${params.toString()}` : "";
      return {
        method: "GET",
        url: `${apiBaseUrl}/api/v1/community/libraries${qs}`,
        body: null,
        hasBody: false,
      };
    }

    if (activeEndpoint === "remix-community") {
      const id = remixForm.libraryId || "LIBRARY_ID";
      const body: Record<string, unknown> = {
        auto_export: remixForm.auto_export === "true",
      };
      if (remixForm.webhook_url) body.webhook_url = remixForm.webhook_url;
      return {
        method: "POST",
        url: `${apiBaseUrl}/api/v1/community/library/${id}/remix`,
        body,
        hasBody: true,
      };
    }

    if (activeEndpoint === "create-clip") {
      const id = createClipForm.projectId || "PROJECT_ID";
      const body: Record<string, unknown> = {
        start_time: parseFloat(createClipForm.start_time) || 0,
        end_time: parseFloat(createClipForm.end_time) || 0,
        auto_render: createClipForm.auto_render === "true",
      };
      if (createClipForm.name) body.name = createClipForm.name;
      return {
        method: "POST",
        url: `${apiBaseUrl}/api/v1/project/${id}/clip`,
        body,
        hasBody: true,
      };
    }

    if (activeEndpoint === "render-clip") {
      const id = renderClipForm.clipId || "CLIP_ID";
      const body: Record<string, unknown> = {};
      if (renderClipForm.brand_kit_id) body.brand_kit_id = renderClipForm.brand_kit_id;
      return {
        method: "POST",
        url: `${apiBaseUrl}/api/v1/clip/${id}/render`,
        body: Object.keys(body).length > 0 ? body : null,
        hasBody: Object.keys(body).length > 0,
      };
    }

    if (activeEndpoint === "get-channel") {
      const id = channelLookupForm.channelId || "CHANNEL_ID";
      return {
        method: "GET",
        url: `${apiBaseUrl}/api/v1/community/channel/${id}`,
        body: null,
        hasBody: false,
      };
    }

    if (activeEndpoint === "channel-libraries") {
      const { channelId, ...rest } = channelLibrariesForm;
      const id = channelId || "CHANNEL_ID";
      const params = new URLSearchParams();
      Object.entries(rest).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const qs = params.toString() ? `?${params.toString()}` : "";
      return {
        method: "GET",
        url: `${apiBaseUrl}/api/v1/community/channel/${id}/libraries${qs}`,
        body: null,
        hasBody: false,
      };
    }

    if (activeEndpoint === "check-url") {
      const params = new URLSearchParams();
      if (checkUrlForm.url) params.append("url", checkUrlForm.url);
      const qs = params.toString() ? `?${params.toString()}` : "";
      return {
        method: "GET",
        url: `${apiBaseUrl}/api/v1/community/check${qs}`,
        body: null,
        hasBody: false,
      };
    }

    // get-project
    const id = getProjectForm.projectId || "PROJECT_ID";
    return {
      method: "GET",
      url: `${apiBaseUrl}/api/v1/project/${id}`,
      body: null,
      hasBody: false,
    };
  };

  const generateCodeSnippets = () => {
    const { method, url, body, hasBody } = getRequestInfo();
    const apiKey = selectedApiKey || "YOUR_API_KEY";
    const bodyJson = body ? JSON.stringify(body, null, 2) : "";

    const snippets: Record<string, string> = {};

    // cURL
    const curlParts = [`curl -X ${method} '${url}'`];
    curlParts.push(`  -H 'Authorization: API-Key ${apiKey}'`);
    if (hasBody) {
      curlParts.push(`  -H 'Content-Type: application/json'`);
      curlParts.push(`  -d '${bodyJson}'`);
    }
    snippets.curl = curlParts.join(" \\\n");

    // Python
    const pythonLines = ["import requests", ""];
    pythonLines.push(`url = "${url}"`);
    pythonLines.push(`headers = {`);
    pythonLines.push(`    "Authorization": "API-Key ${apiKey}",`);
    if (hasBody) pythonLines.push(`    "Content-Type": "application/json",`);
    pythonLines.push(`}`);
    if (hasBody) {
      pythonLines.push("");
      pythonLines.push(`data = ${bodyJson.replace(/\n/g, "\n")}`);
      pythonLines.push("");
      pythonLines.push(`response = requests.post(url, headers=headers, json=data)`);
    } else {
      pythonLines.push("");
      pythonLines.push(`response = requests.get(url, headers=headers)`);
    }
    pythonLines.push(`print(response.json())`);
    snippets.python = pythonLines.join("\n");

    // JavaScript (fetch)
    const jsLines = [];
    if (hasBody) {
      jsLines.push(`const response = await fetch("${url}", {`);
      jsLines.push(`  method: "${method}",`);
      jsLines.push(`  headers: {`);
      jsLines.push(`    "Authorization": "API-Key ${apiKey}",`);
      jsLines.push(`    "Content-Type": "application/json",`);
      jsLines.push(`  },`);
      jsLines.push(`  body: JSON.stringify(${bodyJson.split("\n").map((l, i) => i === 0 ? l : "  " + l).join("\n")}),`);
      jsLines.push(`});`);
    } else {
      jsLines.push(`const response = await fetch("${url}", {`);
      jsLines.push(`  headers: {`);
      jsLines.push(`    "Authorization": "API-Key ${apiKey}",`);
      jsLines.push(`  },`);
      jsLines.push(`});`);
    }
    jsLines.push("");
    jsLines.push(`const data = await response.json();`);
    jsLines.push(`console.log(data);`);
    snippets.javascript = jsLines.join("\n");

    // Node.js (axios)
    const nodeLines = [`const axios = require("axios");`, ""];
    if (hasBody) {
      nodeLines.push(`const response = await axios.post("${url}", ${bodyJson.split("\n").map((l, i) => i === 0 ? l : "  " + l).join("\n")}, {`);
      nodeLines.push(`  headers: {`);
      nodeLines.push(`    "Authorization": "API-Key ${apiKey}",`);
      nodeLines.push(`  },`);
      nodeLines.push(`});`);
    } else {
      nodeLines.push(`const response = await axios.get("${url}", {`);
      nodeLines.push(`  headers: {`);
      nodeLines.push(`    "Authorization": "API-Key ${apiKey}",`);
      nodeLines.push(`  },`);
      nodeLines.push(`});`);
    }
    nodeLines.push("");
    nodeLines.push(`console.log(response.data);`);
    snippets.node = nodeLines.join("\n");

    // PHP
    const phpLines = ["<?php", ""];
    phpLines.push(`$ch = curl_init("${url}");`);
    phpLines.push(`curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);`);
    if (hasBody) {
      phpLines.push(`curl_setopt($ch, CURLOPT_POST, true);`);
      phpLines.push(`curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(${bodyJson.split("\n").map((l, i) => i === 0 ? l : "  " + l).join("\n")}));`);
    }
    phpLines.push(`curl_setopt($ch, CURLOPT_HTTPHEADER, [`);
    phpLines.push(`    "Authorization: API-Key ${apiKey}",`);
    if (hasBody) phpLines.push(`    "Content-Type: application/json",`);
    phpLines.push(`]);`);
    phpLines.push("");
    phpLines.push(`$response = curl_exec($ch);`);
    phpLines.push(`curl_close($ch);`);
    phpLines.push("");
    phpLines.push(`$data = json_decode($response, true);`);
    phpLines.push(`print_r($data);`);
    snippets.php = phpLines.join("\n");

    return snippets;
  };

  const codeLanguages = [
    { id: "curl", label: "cURL", hljs: "bash" },
    { id: "python", label: "Python", hljs: "python" },
    { id: "javascript", label: "JavaScript", hljs: "javascript" },
    { id: "node", label: "Node.js", hljs: "javascript" },
    { id: "php", label: "PHP", hljs: "php" },
  ] as const;

  const snippets = generateCodeSnippets();

  const highlightedSnippets = useMemo(() => {
    const result: Record<string, string> = {};
    for (const lang of codeLanguages) {
      result[lang.id] = hljs.highlight(snippets[lang.id], { language: lang.hljs }).value;
    }
    return result;
  }, [snippets.curl, snippets.python, snippets.javascript, snippets.node, snippets.php]);

  const formatOptions = [
    { value: "auto", label: t('apiPlayground.formatAuto') },
    { value: "max_1_min", label: t('apiPlayground.formatMax1Min') },
    { value: "max_30_sec", label: t('apiPlayground.formatMax30Sec') }
  ];

  const languageOptions = [
    { value: "pt-BR", label: t('apiPlayground.langPtBr') },
    { value: "en-US", label: t('apiPlayground.langEnUs') },
    { value: "es-ES", label: t('apiPlayground.langEsEs') }
  ];

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/api-documentation">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{t('apiPlayground.title')}</h1>
            <p className="text-muted-foreground">
              {t('apiPlayground.subtitle')}
            </p>
          </div>
        </div>

        {/* API Key Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              {t('apiPlayground.authentication')}
            </CardTitle>
            <CardDescription>
              {t('apiPlayground.selectApiKeyDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {apiKeysLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('apiPlayground.loadingApiKeys')}</span>
              </div>
            ) : apiKeysData?.myAPIKeys?.length ? (
              <div className="space-y-2">
                <Label htmlFor="api-key-select">{t('apiPlayground.chooseApiKey')}</Label>
                <Select value={selectedApiKey} onValueChange={setSelectedApiKey}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('apiPlayground.selectApiKey')} />
                  </SelectTrigger>
                  <SelectContent>
                    {apiKeysData.myAPIKeys.map((key) => (
                      <SelectItem key={key.id} value={key.key}>
                        {key.name} - {key.key.substring(0, 20)}...
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t('apiPlayground.noApiKeys')}
                  <Button asChild variant="link" className="p-0 h-auto ml-1">
                    <Link to="/api-keys">{t('apiPlayground.createOneHere')}</Link>
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Request Panel */}
          <Card>
            <CardHeader>
              <CardTitle>{t('apiPlayground.makeRequest')}</CardTitle>
              <CardDescription>
                {t('apiPlayground.configureAndSend')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeEndpoint} onValueChange={setActiveEndpoint} className="space-y-4">
                <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 lg:grid-cols-10 h-auto">
                  {endpoints.map((endpoint) => (
                    <TabsTrigger key={endpoint.id} value={endpoint.id} className="text-xs">
                      {endpoint.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* Create Project */}
                <TabsContent value="create-project" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="default">POST</Badge>
                    <code className="text-sm">/api/v1/project</code>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="video-url">{t('apiPlayground.videoUrl')}</Label>
                      <Input
                        id="video-url"
                        value={createProjectForm.base_video_url}
                        onChange={(e) => setCreateProjectForm(prev => ({ ...prev, base_video_url: e.target.value }))}
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                    </div>

                    <div>
                      <Label htmlFor="project-name">{t('apiPlayground.projectName')}</Label>
                      <Input
                        id="project-name"
                        value={createProjectForm.name}
                        onChange={(e) => setCreateProjectForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="My Project"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="format">{t('apiPlayground.format')}</Label>
                        <Select
                          value={createProjectForm.format}
                          onValueChange={(value) => setCreateProjectForm(prev => ({ ...prev, format: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {formatOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="language">{t('apiPlayground.language')}</Label>
                        <Select
                          value={createProjectForm.language}
                          onValueChange={(value) => setCreateProjectForm(prev => ({ ...prev, language: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {languageOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="webhook-url">{t('apiPlayground.webhookUrl')}</Label>
                      <Input
                        id="webhook-url"
                        value={createProjectForm.webhook_url}
                        onChange={(e) => setCreateProjectForm(prev => ({ ...prev, webhook_url: e.target.value }))}
                        placeholder="https://your-webhook.com/callback"
                      />
                    </div>

                    <div>
                      <Label htmlFor="prompt">{t('apiPlayground.prompt')}</Label>
                      <Textarea
                        id="prompt"
                        value={createProjectForm.prompt}
                        onChange={(e) => setCreateProjectForm(prev => ({ ...prev, prompt: e.target.value }))}
                        placeholder={t('apiPlayground.promptPlaceholder')}
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('apiPlayground.brandKitId')}</Label>
                      {brandKitsLoading ? (
                        <div className="h-10 border border-border rounded-md flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div
                            className={`cursor-pointer rounded-lg border-2 transition-colors p-2 text-center text-xs ${
                              !createProjectForm.brand_kit_id
                                ? 'border-primary bg-primary/10'
                                : 'border-gray-600 hover:border-gray-400'
                            }`}
                            onClick={() => setCreateProjectForm(prev => ({ ...prev, brand_kit_id: "" }))}
                          >
                            {t('apiPlayground.brandKitDefault')}
                          </div>
                          {(brandKitsData?.myBrandKits || []).length > 0 && (
                            <BrandKitScrollRow
                              kits={brandKitsData?.myBrandKits || []}
                              selectedId={createProjectForm.brand_kit_id}
                              onSelect={(id) => setCreateProjectForm(prev => ({ ...prev, brand_kit_id: id }))}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateProject}
                    disabled={loading || !selectedApiKey}
                    className="w-full"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {t('apiPlayground.createProject')}
                  </Button>
                </TabsContent>

                {/* List Projects */}
                <TabsContent value="list-projects" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/projects</code>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="limit">{t('apiPlayground.limit')}</Label>
                      <Input
                        id="limit"
                        type="number"
                        value={listProjectsForm.limit}
                        onChange={(e) => setListProjectsForm(prev => ({ ...prev, limit: e.target.value }))}
                        placeholder="10"
                        min="1"
                        max="100"
                      />
                    </div>

                    <div>
                      <Label htmlFor="offset">{t('apiPlayground.offset')}</Label>
                      <Input
                        id="offset"
                        type="number"
                        value={listProjectsForm.offset}
                        onChange={(e) => setListProjectsForm(prev => ({ ...prev, offset: e.target.value }))}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleListProjects}
                    disabled={loading || !selectedApiKey}
                    className="w-full"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {t('apiPlayground.listProjects')}
                  </Button>
                </TabsContent>

                {/* Get Project */}
                <TabsContent value="get-project" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/project/{"{id}"}</code>
                  </div>

                  <div>
                    <Label htmlFor="project-id">{t('apiPlayground.projectId')}</Label>
                    <Input
                      id="project-id"
                      value={getProjectForm.projectId}
                      onChange={(e) => setGetProjectForm(prev => ({ ...prev, projectId: e.target.value }))}
                      placeholder={t('apiPlayground.projectIdPlaceholder')}
                    />
                  </div>

                  <Button
                    onClick={handleGetProject}
                    disabled={loading || !selectedApiKey || !getProjectForm.projectId}
                    className="w-full"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    {t('apiPlayground.getProject')}
                  </Button>
                </TabsContent>

                {/* List Community Libraries */}
                <TabsContent value="list-community" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/community/libraries</code>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cl-limit">Limit (1-100)</Label>
                      <Input id="cl-limit" type="number" value={communityListForm.limit}
                        onChange={(e) => setCommunityListForm(p => ({ ...p, limit: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="cl-offset">Offset</Label>
                      <Input id="cl-offset" type="number" value={communityListForm.offset}
                        onChange={(e) => setCommunityListForm(p => ({ ...p, offset: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cl-search">Search</Label>
                    <Input id="cl-search" value={communityListForm.search}
                      onChange={(e) => setCommunityListForm(p => ({ ...p, search: e.target.value }))}
                      placeholder="title or channel name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cl-channel">channel_id</Label>
                      <Input id="cl-channel" value={communityListForm.channel_id}
                        onChange={(e) => setCommunityListForm(p => ({ ...p, channel_id: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="cl-tag">tag_id</Label>
                      <Input id="cl-tag" value={communityListForm.tag_id}
                        onChange={(e) => setCommunityListForm(p => ({ ...p, tag_id: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cl-sort">Sort</Label>
                    <Select value={communityListForm.sort}
                      onValueChange={(v) => setCommunityListForm(p => ({ ...p, sort: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">newest</SelectItem>
                        <SelectItem value="oldest">oldest</SelectItem>
                        <SelectItem value="views">views</SelectItem>
                        <SelectItem value="subscribers">subscribers</SelectItem>
                        <SelectItem value="remixes">remixes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleListCommunityLibraries} disabled={loading || !selectedApiKey} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    List Community Libraries
                  </Button>
                </TabsContent>

                {/* Remix Community Library */}
                <TabsContent value="remix-community" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="default">POST</Badge>
                    <code className="text-sm">/api/v1/community/library/{"{id}"}/remix</code>
                  </div>
                  <div>
                    <Label htmlFor="remix-id">Community library ID *</Label>
                    <Input id="remix-id" value={remixForm.libraryId}
                      onChange={(e) => setRemixForm(p => ({ ...p, libraryId: e.target.value }))}
                      placeholder="From GET /community/libraries" />
                  </div>
                  <div>
                    <Label htmlFor="remix-webhook">Webhook URL</Label>
                    <Input id="remix-webhook" value={remixForm.webhook_url}
                      onChange={(e) => setRemixForm(p => ({ ...p, webhook_url: e.target.value }))}
                      placeholder="https://your-webhook.com/callback" />
                  </div>
                  <div>
                    <Label htmlFor="remix-export">auto_export</Label>
                    <Select value={remixForm.auto_export}
                      onValueChange={(v) => setRemixForm(p => ({ ...p, auto_export: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="false">false (curate only, default)</SelectItem>
                        <SelectItem value="true">true (curate + render)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleRemixCommunityLibrary} disabled={loading || !selectedApiKey || !remixForm.libraryId} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Remix
                  </Button>
                </TabsContent>

                {/* Create Manual Clip */}
                <TabsContent value="create-clip" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="default">POST</Badge>
                    <code className="text-sm">/api/v1/project/{"{id}"}/clip</code>
                  </div>
                  <div>
                    <Label htmlFor="cc-project">Project ID *</Label>
                    <Input id="cc-project" value={createClipForm.projectId}
                      onChange={(e) => setCreateClipForm(p => ({ ...p, projectId: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cc-start">start_time (s) *</Label>
                      <Input id="cc-start" type="number" step="0.1" value={createClipForm.start_time}
                        onChange={(e) => setCreateClipForm(p => ({ ...p, start_time: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="cc-end">end_time (s) *</Label>
                      <Input id="cc-end" type="number" step="0.1" value={createClipForm.end_time}
                        onChange={(e) => setCreateClipForm(p => ({ ...p, end_time: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cc-name">Name</Label>
                    <Input id="cc-name" value={createClipForm.name}
                      onChange={(e) => setCreateClipForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div>
                    <Label htmlFor="cc-render">auto_render</Label>
                    <Select value={createClipForm.auto_render}
                      onValueChange={(v) => setCreateClipForm(p => ({ ...p, auto_render: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">true (render immediately, default)</SelectItem>
                        <SelectItem value="false">false (create only)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreateProjectClip} disabled={loading || !selectedApiKey} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Create Clip
                  </Button>
                </TabsContent>

                {/* Get Community Channel */}
                <TabsContent value="get-channel" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/community/channel/{"{id}"}</code>
                  </div>
                  <div>
                    <Label htmlFor="gc-id">Channel ID *</Label>
                    <Input id="gc-id" value={channelLookupForm.channelId}
                      onChange={(e) => setChannelLookupForm(p => ({ ...p, channelId: e.target.value }))}
                      placeholder="From GET /community/channels" />
                  </div>
                  <Button onClick={handleGetCommunityChannel} disabled={loading || !selectedApiKey || !channelLookupForm.channelId} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Get Channel
                  </Button>
                </TabsContent>

                {/* Channel Libraries */}
                <TabsContent value="channel-libraries" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/community/channel/{"{id}"}/libraries</code>
                  </div>
                  <div>
                    <Label htmlFor="cl-channel-id">Channel ID *</Label>
                    <Input id="cl-channel-id" value={channelLibrariesForm.channelId}
                      onChange={(e) => setChannelLibrariesForm(p => ({ ...p, channelId: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="clp-limit">Limit (1-100)</Label>
                      <Input id="clp-limit" type="number" value={channelLibrariesForm.limit}
                        onChange={(e) => setChannelLibrariesForm(p => ({ ...p, limit: e.target.value }))} />
                    </div>
                    <div>
                      <Label htmlFor="clp-offset">Offset</Label>
                      <Input id="clp-offset" type="number" value={channelLibrariesForm.offset}
                        onChange={(e) => setChannelLibrariesForm(p => ({ ...p, offset: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="clp-search">Search</Label>
                    <Input id="clp-search" value={channelLibrariesForm.search}
                      onChange={(e) => setChannelLibrariesForm(p => ({ ...p, search: e.target.value }))}
                      placeholder="title substring" />
                  </div>
                  <div>
                    <Label htmlFor="clp-tag">tag_id</Label>
                    <Input id="clp-tag" value={channelLibrariesForm.tag_id}
                      onChange={(e) => setChannelLibrariesForm(p => ({ ...p, tag_id: e.target.value }))} />
                  </div>
                  <div>
                    <Label htmlFor="clp-sort">Sort</Label>
                    <Select value={channelLibrariesForm.sort}
                      onValueChange={(v) => setChannelLibrariesForm(p => ({ ...p, sort: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">newest</SelectItem>
                        <SelectItem value="oldest">oldest</SelectItem>
                        <SelectItem value="views">views</SelectItem>
                        <SelectItem value="subscribers">subscribers</SelectItem>
                        <SelectItem value="remixes">remixes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleListChannelLibraries} disabled={loading || !selectedApiKey || !channelLibrariesForm.channelId} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    List Channel Videos
                  </Button>
                </TabsContent>

                {/* Check Community Library by URL */}
                <TabsContent value="check-url" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary">GET</Badge>
                    <code className="text-sm">/api/v1/community/check</code>
                  </div>
                  <div>
                    <Label htmlFor="ck-url">Video URL *</Label>
                    <Input id="ck-url" value={checkUrlForm.url}
                      onChange={(e) => setCheckUrlForm(p => ({ ...p, url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..." />
                  </div>
                  <Button onClick={handleCheckCommunityLibrary} disabled={loading || !selectedApiKey || !checkUrlForm.url} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Check URL
                  </Button>
                </TabsContent>

                {/* Render Clip */}
                <TabsContent value="render-clip" className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="default">POST</Badge>
                    <code className="text-sm">/api/v1/clip/{"{id}"}/render</code>
                  </div>
                  <div>
                    <Label htmlFor="rc-clip">Clip ID *</Label>
                    <Input id="rc-clip" value={renderClipForm.clipId}
                      onChange={(e) => setRenderClipForm(p => ({ ...p, clipId: e.target.value }))} />
                  </div>
                  <div>
                    <Label htmlFor="rc-brand">brand_kit_id</Label>
                    <Input id="rc-brand" value={renderClipForm.brand_kit_id}
                      onChange={(e) => setRenderClipForm(p => ({ ...p, brand_kit_id: e.target.value }))}
                      placeholder={t('apiPlayground.brandKitOverridePlaceholder')} />
                  </div>
                  <Button onClick={handleRenderClip} disabled={loading || !selectedApiKey || !renderClipForm.clipId} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Render
                  </Button>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Response Panel */}
          <Card>
            <CardHeader>
              <CardTitle>{t('apiPlayground.response')}</CardTitle>
              <CardDescription>
                {t('apiPlayground.responseDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Request Info */}
              {requestUrl && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">{t('apiPlayground.requestUrl')}</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(requestUrl)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <code className="block bg-muted p-2 rounded text-xs break-all">
                    {requestUrl}
                  </code>
                </div>
              )}

              {requestBody && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">{t('apiPlayground.requestBody')}</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(requestBody)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
                    <code>{requestBody}</code>
                  </pre>
                </div>
              )}

              {/* Response */}
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <span className="ml-2">{t('apiPlayground.makingRequest')}</span>
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{t('apiPlayground.error')}</strong> {error}
                  </AlertDescription>
                </Alert>
              )}

              {response && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {t('apiPlayground.response')}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(JSON.stringify(response, null, 2))}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-96">
                    <code>{JSON.stringify(response, null, 2)}</code>
                  </pre>
                </div>
              )}

              {!loading && !error && !response && (
                <div className="text-center py-8 text-muted-foreground">
                  <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('apiPlayground.emptyResponse')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Code Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              {t('apiPlayground.codePreview')}
            </CardTitle>
            <CardDescription>
              {t('apiPlayground.codePreviewDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeLanguage} onValueChange={setActiveLanguage}>
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  {codeLanguages.map((lang) => (
                    <TabsTrigger key={lang.id} value={lang.id} className="text-xs">
                      {lang.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(snippets[activeLanguage])}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  {t('apiPlayground.copy')}
                </Button>
              </div>
              {codeLanguages.map((lang) => (
                <TabsContent key={lang.id} value={lang.id} className="mt-0">
                  <pre className="bg-[#0d1117] p-4 rounded text-xs overflow-x-auto max-h-96">
                    <code
                      className="hljs"
                      dangerouslySetInnerHTML={{ __html: highlightedSnippets[lang.id] }}
                    />
                  </pre>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Help Links */}
        <Card>
          <CardHeader>
            <CardTitle>{t('apiPlayground.needHelp')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-getting-started">
                  <div className="flex items-start gap-3">
                    <Play className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiPlayground.gettingStarted')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiPlayground.learnBasics')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-reference">
                  <div className="flex items-start gap-3">
                    <ExternalLink className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiPlayground.apiReference')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiPlayground.completeDocumentation')}</div>
                    </div>
                  </div>
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto p-4 justify-start">
                <Link to="/api-keys">
                  <div className="flex items-start gap-3">
                    <Key className="w-5 h-5 mt-0.5" />
                    <div className="text-left">
                      <div className="font-medium">{t('apiPlayground.manageApiKeys')}</div>
                      <div className="text-sm text-muted-foreground">{t('apiPlayground.createAndManageKeys')}</div>
                    </div>
                  </div>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ApiPlayground;
